import React, { useState, useEffect, useRef } from 'react';
import * as faceapi from 'face-api.js';
import { attendanceService } from '../services/api';
import toast from 'react-hot-toast';
import { Camera, User, LogIn, LogOut, Coffee, ArrowLeftFromLine, CheckCircle, XCircle, Loader2, Clock, AlertTriangle } from 'lucide-react';

const EVENT_CONFIG = [
    { type: 'entry', label: 'ENTRADA', icon: LogIn, color: 'bg-green-600 hover:bg-green-700', desc: 'Registrar llegada' },
    { type: 'lunch_out', label: 'ALMUERZO', icon: Coffee, color: 'bg-orange-500 hover:bg-orange-600', desc: 'Salir a almorzar' },
    { type: 'lunch_in', label: 'REGRESO', icon: ArrowLeftFromLine, color: 'bg-blue-600 hover:bg-blue-700', desc: 'Volver del almuerzo' },
    { type: 'exit', label: 'SALIDA', icon: LogOut, color: 'bg-red-600 hover:bg-red-700', desc: 'Fin de jornada' },
];

const AttendancePage = () => {
    const [modelsLoaded, setModelsLoaded] = useState(false);
    const [identification, setIdentification] = useState('');
    const [employee, setEmployee] = useState(null);
    const [todayStatus, setTodayStatus] = useState(null);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [faceDetected, setFaceDetected] = useState(false);
    const [matchingFace, setMatchingFace] = useState(false);

    const videoRef = useRef();
    const detectionIntervalRef = useRef();

    // Load face-api models
    useEffect(() => {
        const loadModels = async () => {
            try {
                const MODEL_URL = '/models';
                await Promise.all([
                    faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
                    faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
                    faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
                ]);
                setModelsLoaded(true);
                startVideo();
            } catch (error) {
                console.error('Error cargando modelos:', error);
                toast.error('Error al cargar el sistema de reconocimiento facial');
            }
        };
        loadModels();

        return () => {
            if (detectionIntervalRef.current) clearInterval(detectionIntervalRef.current);
            stopVideo();
        };
    }, []);

    const startVideo = () => {
        navigator.mediaDevices.getUserMedia({ video: {} })
            .then(stream => {
                if (videoRef.current) videoRef.current.srcObject = stream;
            })
            .catch(err => {
                console.error('Error accediendo a la cámara:', err);
                toast.error('No se pudo acceder a la cámara');
            });
    };

    const stopVideo = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            videoRef.current.srcObject.getTracks().forEach(track => track.stop());
        }
    };

    // Face detection loop
    useEffect(() => {
        if (!modelsLoaded || !videoRef.current) return;

        const detect = async () => {
            if (!videoRef.current || videoRef.current.paused || videoRef.current.ended) return;

            const detections = await faceapi.detectSingleFace(
                videoRef.current,
                new faceapi.TinyFaceDetectorOptions()
            ).withFaceLandmarks().withFaceDescriptor();

            if (detections) {
                setFaceDetected(true);
                if (employee && employee.face_descriptors) {
                    const distances = employee.face_descriptors.map(desc =>
                        faceapi.euclideanDistance(detections.descriptor, new Float32Array(desc))
                    );
                    const minDistance = Math.min(...distances);
                    setMatchingFace(minDistance < 0.55);
                }
            } else {
                setFaceDetected(false);
                setMatchingFace(false);
            }
        };

        detectionIntervalRef.current = setInterval(detect, 500);
        return () => clearInterval(detectionIntervalRef.current);
    }, [modelsLoaded, employee]);

    const handleVerifyIdentification = async (e) => {
        e.preventDefault();
        if (!identification) return;

        setIsVerifying(true);
        setEmployee(null);
        setTodayStatus(null);
        setMatchingFace(false);

        try {
            const response = await attendanceService.getEmployeeByIdentification(identification);
            if (response.success) {
                setEmployee(response.data);
                toast.success(`Empleado: ${response.data.full_name}`);
                // Fetch today's status
                const statusResp = await attendanceService.getEmployeeStatusToday(response.data.id);
                if (statusResp.success) {
                    setTodayStatus(statusResp.data);
                }
            } else {
                toast.error('Empleado no encontrado');
            }
        } catch (error) {
            console.error(error);
            toast.error('Empleado no encontrado');
        } finally {
            setIsVerifying(false);
        }
    };

    const recordEvent = async (type) => {
        if (!employee || !matchingFace || isRecording) return;

        setIsRecording(true);
        try {
            const canvas = document.createElement('canvas');
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(videoRef.current, 0, 0);

            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.8));
            const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });

            const formData = new FormData();
            formData.append('employee_id', employee.id);
            formData.append('type', type);
            formData.append('photo', file);

            const eventLabels = { entry: 'Entrada', lunch_out: 'Salida Almuerzo', lunch_in: 'Regreso Almuerzo', exit: 'Salida' };

            const response = await attendanceService.recordAttendance(formData);
            if (response.success) {
                toast.success(`${eventLabels[type]} registrada exitosamente ✅`, { duration: 5000 });
                setIdentification('');
                setEmployee(null);
                setTodayStatus(null);
                setMatchingFace(false);
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Error al registrar');
        } finally {
            setIsRecording(false);
        }
    };

    const isEventDone = (type) => todayStatus?.registeredEvents?.includes(type);
    const nextEvent = todayStatus?.nextEvent;

    return (
        <div className="min-h-screen bg-gray-900 text-white p-4 flex flex-col items-center justify-center">
            <div className="max-w-5xl w-full grid grid-cols-1 md:grid-cols-2 gap-8 items-start">

                {/* Camera Section */}
                <div className="relative rounded-2xl overflow-hidden bg-black shadow-2xl border-2 border-gray-700 aspect-video md:aspect-square flex items-center justify-center">
                    {!modelsLoaded && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800 z-10">
                            <Loader2 className="w-12 h-12 animate-spin text-primary-500 mb-4" />
                            <p>Cargando Inteligencia Artificial...</p>
                        </div>
                    )}

                    <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover mirror" />

                    <div className="absolute top-4 right-4 flex gap-2">
                        {faceDetected ? (
                            <span className="bg-green-500/80 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Rostro Detectado
                            </span>
                        ) : (
                            <span className="bg-red-500/80 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                <XCircle className="w-3 h-3" /> Sin Rostro
                            </span>
                        )}
                    </div>

                    {employee && faceDetected && (
                        <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                            {matchingFace ? (
                                <span className="bg-green-600 px-6 py-2 rounded-full font-bold shadow-lg animate-pulse flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5" /> Identidad Verificada
                                </span>
                            ) : (
                                <span className="bg-orange-500 px-6 py-2 rounded-full font-bold shadow-lg flex items-center gap-2">
                                    <Loader2 className="w-5 h-5 animate-spin" /> Verificando Identidad...
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Controls Section */}
                <div className="space-y-6">
                    <div className="bg-gray-800 p-8 rounded-2xl shadow-xl border border-gray-700">
                        <h1 className="text-2xl font-bold mb-6 flex items-center gap-2 text-primary-400">
                            <Camera className="w-8 h-8" /> Control de Asistencia
                        </h1>

                        {!employee ? (
                            <form onSubmit={handleVerifyIdentification} className="space-y-4">
                                <label className="block text-sm font-medium text-gray-400">
                                    Ingresa tu número de Cédula
                                </label>
                                <div className="relative">
                                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                                    <input
                                        type="text"
                                        value={identification}
                                        onChange={(e) => setIdentification(e.target.value)}
                                        className="w-full bg-gray-900 border-2 border-gray-700 rounded-xl py-4 pl-12 pr-4 text-xl focus:border-primary-500 outline-none transition-all"
                                        placeholder="123456789"
                                        autoFocus
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={isVerifying || !identification}
                                    className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-700 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2"
                                >
                                    {isVerifying ? <Loader2 className="w-6 h-6 animate-spin" /> : 'Siguiente'}
                                </button>
                            </form>
                        ) : (
                            <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4">
                                {/* Employee info */}
                                <div className="text-center">
                                    <p className="text-gray-400 text-sm uppercase tracking-widest">Empleado</p>
                                    <h2 className="text-2xl font-black text-white">{employee.full_name}</h2>
                                    <p className="text-gray-500 mt-1">C.C. {employee.identification}</p>
                                </div>

                                {/* Tardiness warning */}
                                {todayStatus?.tardinessMinutes > 0 && (
                                    <div className="bg-red-900/30 border border-red-700/50 px-4 py-2 rounded-lg flex items-center gap-2 text-red-300 text-sm">
                                        <AlertTriangle className="w-4 h-4" />
                                        Llegaste <strong>{todayStatus.tardinessMinutes} minutos</strong> tarde hoy
                                    </div>
                                )}

                                {/* Schedule info */}
                                {todayStatus?.schedule && !todayStatus.schedule.is_day_off && (
                                    <div className="bg-gray-700/50 px-4 py-2 rounded-lg text-xs text-gray-300 flex items-center gap-2">
                                        <Clock className="w-3.5 h-3.5 text-blue-400" />
                                        Horario: {todayStatus.schedule.start_time?.slice(0, 5)} - {todayStatus.schedule.end_time?.slice(0, 5)}
                                    </div>
                                )}

                                {/* 4 Event Buttons */}
                                {todayStatus?.allComplete ? (
                                    <div className="text-center py-6">
                                        <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-3" />
                                        <p className="text-xl font-bold text-green-400">Jornada Completa</p>
                                        <p className="text-gray-500 text-sm mt-1">Todos los registros del día están completos</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 gap-3">
                                        {EVENT_CONFIG.map(evt => {
                                            const done = isEventDone(evt.type);
                                            const isNext = nextEvent === evt.type;
                                            const Icon = evt.icon;

                                            return (
                                                <button
                                                    key={evt.type}
                                                    onClick={() => recordEvent(evt.type)}
                                                    disabled={done || !matchingFace || isRecording}
                                                    className={`relative flex flex-col items-center justify-center gap-2 py-6 rounded-xl transition-all group
                                                        ${done
                                                            ? 'bg-gray-700/50 opacity-40 cursor-not-allowed'
                                                            : isNext
                                                                ? `${evt.color} ring-2 ring-white/50 shadow-lg scale-105`
                                                                : `${evt.color} disabled:opacity-20 disabled:grayscale`
                                                        }`}
                                                >
                                                    {done && (
                                                        <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-green-400" />
                                                    )}
                                                    {isNext && !done && (
                                                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-white text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                                            SIGUIENTE
                                                        </span>
                                                    )}
                                                    <Icon className={`w-8 h-8 ${!done ? 'group-hover:scale-110' : ''} transition-transform`} />
                                                    <span className="font-bold text-sm">{evt.label}</span>
                                                    <span className="text-[10px] opacity-70">{evt.desc}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Today's events timeline */}
                                {todayStatus?.events?.length > 0 && (
                                    <div className="bg-gray-700/30 rounded-lg p-3">
                                        <p className="text-xs font-medium text-gray-400 mb-2">Registros de hoy:</p>
                                        <div className="space-y-1">
                                            {todayStatus.events.map((evt, i) => (
                                                <div key={i} className="flex items-center gap-2 text-xs">
                                                    <CheckCircle className="w-3 h-3 text-green-400" />
                                                    <span className="text-gray-300">
                                                        {{ entry: 'Entrada', lunch_out: 'Sal. Almuerzo', lunch_in: 'Reg. Almuerzo', exit: 'Salida' }[evt.type]}
                                                    </span>
                                                    <span className="text-gray-500 ml-auto">
                                                        {new Date(evt.timestamp).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <button
                                    onClick={() => { setEmployee(null); setIdentification(''); setTodayStatus(null); }}
                                    className="w-full py-2 text-gray-500 hover:text-white transition-colors text-sm"
                                >
                                    Cancelar / No soy yo
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="bg-primary-900/20 border border-primary-800/50 p-4 rounded-xl">
                        <p className="text-xs text-primary-300 leading-relaxed">
                            <strong>Consejo:</strong> Asegúrate de estar bien iluminado y mirar directamente a la cámara.
                            El sistema capturará una foto para fines de auditoría.
                        </p>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .mirror { transform: scaleX(-1); }
            `}</style>
        </div>
    );
};

export default AttendancePage;
